import { cn } from '@/lib/utils';
import { formatarInteiro, formatarPercentual } from '@/features/relatorios/formato';
import { corDaSerie } from './paleta';

export interface FatiaRosca {
  chave: string;
  rotulo: string;
  valor: number;
}

interface Props {
  fatias: FatiaRosca[];
  /** Palavra que descreve o total no centro ("solicitações", "agendamentos"). */
  unidade: string;
  vazio?: string;
}

const RAIO = 60;
const ESPESSURA = 18;
const CIRCUNFERENCIA = 2 * Math.PI * RAIO;

/**
 * Distribuição em rosca, com legenda ao lado.
 *
 * Rosca só cabe onde a pergunta é "que fatia do total" e as categorias são
 * poucas e fixas — tipo, prioridade, origem do agendamento. Para ranking, a
 * comparação de ângulos é pior que a de barras, e o gráfico de barras é quem
 * deve ser usado.
 *
 * A legenda repete rótulo, valor e percentual: quem imprime em preto e branco,
 * ou não distingue as cores, continua lendo a mesma informação.
 */
export function GraficoRosca({ fatias, unidade, vazio = 'Sem dados no período.' }: Props) {
  const total = fatias.reduce((soma, fatia) => soma + fatia.valor, 0);

  if (total === 0) {
    return <p className="text-silenciado-texto py-6 text-center text-sm">{vazio}</p>;
  }

  let acumulado = 0;
  const segmentos = fatias.map((fatia, indice) => {
    const proporcao = fatia.valor / total;
    const segmento = {
      ...fatia,
      cor: corDaSerie(indice),
      percentual: Math.round(proporcao * 1000) / 10,
      comprimento: proporcao * CIRCUNFERENCIA,
      // O SVG começa o traço às 3 horas; o giro de -90° põe a primeira fatia
      // no topo, que é onde o olho começa a ler.
      deslocamento: -acumulado * CIRCUNFERENCIA,
    };
    acumulado += proporcao;
    return segmento;
  });

  return (
    <div className="flex flex-wrap items-center gap-5">
      <svg
        viewBox="0 0 160 160"
        className="size-36 shrink-0"
        role="img"
        aria-label={`Distribuição de ${formatarInteiro(total)} ${unidade}.`}
      >
        <g transform="rotate(-90 80 80)">
          <circle
            cx={80}
            cy={80}
            r={RAIO}
            fill="none"
            className="stroke-silenciado"
            strokeWidth={ESPESSURA}
          />
          {segmentos.map((segmento) => (
            <circle
              key={segmento.chave}
              cx={80}
              cy={80}
              r={RAIO}
              fill="none"
              className={segmento.cor.traco}
              strokeWidth={ESPESSURA}
              strokeDasharray={`${segmento.comprimento} ${CIRCUNFERENCIA - segmento.comprimento}`}
              strokeDashoffset={segmento.deslocamento}
            >
              <title>{`${segmento.rotulo}: ${formatarInteiro(segmento.valor)}`}</title>
            </circle>
          ))}
        </g>
        <text
          x={80}
          y={76}
          textAnchor="middle"
          className="fill-cartao-texto text-[20px] font-semibold"
        >
          {formatarInteiro(total)}
        </text>
        <text x={80} y={94} textAnchor="middle" className="fill-silenciado-texto text-[10px]">
          {unidade}
        </text>
      </svg>

      <ul className="flex min-w-40 flex-1 flex-col gap-1.5 text-sm">
        {segmentos.map((segmento) => (
          <li key={segmento.chave} className="flex items-center gap-2">
            <span
              className={cn('size-2.5 shrink-0 rounded-full', segmento.cor.fundo)}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate">{segmento.rotulo}</span>
            <span className="text-silenciado-texto text-xs tabular-nums">
              {formatarPercentual(segmento.percentual)}
            </span>
            <span className="font-semibold tabular-nums">{formatarInteiro(segmento.valor)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
